import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import folderDocumentsApi, {
  DocumentFolderDto,
  CreateFolderDocumentRequestDto,
} from '../../services/folderDocumentService';

// ============================================================================
// STYLED COMPONENTS - Subtle, elegant design inspired by PublicFolderUploadPage
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.15s ease;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: #008EA2;
    box-shadow: 0 0 0 2px rgba(0, 142, 162, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  resize: vertical;
  min-height: 70px;
  font-family: inherit;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: #008EA2;
    box-shadow: 0 0 0 2px rgba(0, 142, 162, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  transition: all 0.15s ease;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #008EA2;
  }
`;

const AccessCodeRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: stretch;
`;

const AccessCodeInput = styled(Input)`
  flex: 1;
  font-family: monospace;
  letter-spacing: 2px;
`;

const GenerateCodeButton = styled.button`
  padding: 12px 16px;
  background: #008EA2;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    background: #0060c7;
  }
`;

const HelpText = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const Divider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const ItemCount = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemNumber = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #9ca3af;
  min-width: 20px;
`;

const ItemFields = styled.div`
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ItemSelect = styled(Select)`
  flex: 1;
  min-width: 0;
`;

const ItemInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: #008EA2;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const MandatoryToggle = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
`;

const Checkbox = styled.span<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid ${props => props.$active ? '#16a34a' : '#d1d5db'};
  background: ${props => props.$active ? '#16a34a' : 'white'};
  transition: all 0.15s ease;
  color: white;
`;

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ToggleLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  padding: 2px;

  &:hover {
    color: #dc2626;
  }
`;

const AddButtonWrapper = styled.div`
  margin-top: 8px;
  margin-right: 32px; /* Align with ItemFields (ItemNumber width + gap) */
`;

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  background: transparent;
  border: none;
  font-size: 13px;
  font-weight: 500;
  color: #008EA2;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #0060c7;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 13px;
  color: #dc2626;
`;

const Footer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  ${props => props.variant === 'primary' ? `
    background: #008EA2;
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background: #0060c7;
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #e5e7eb;

    &:hover:not(:disabled) {
      background: #f9fafb;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// SVG Icons
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3,6 5,6 21,6"/>
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface NewDocumentRequestModalProps {
  clientId: number;
  folders: DocumentFolderDto[];
  onClose: () => void;
  onCreated: () => void;
}

interface RequestItemForm {
  id: string;
  targetFolderId: number;
  specialInstructions: string;
  isMandatory: boolean;
}

// Generate random access code
const generateAccessCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const NewDocumentRequestModal: React.FC<NewDocumentRequestModalProps> = ({
  clientId,
  folders,
  onClose,
  onCreated,
}) => {
  const [requestTitle, setRequestTitle] = useState('');
  const [generalInstructions, setGeneralInstructions] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [items, setItems] = useState<RequestItemForm[]>([
    {
      id: crypto.randomUUID(),
      targetFolderId: 0,
      specialInstructions: '',
      isMandatory: true,
    },
  ]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGenerateCode = () => {
    setAccessCode(generateAccessCode());
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        id: crypto.randomUUID(),
        targetFolderId: 0,
        specialInstructions: '',
        isMandatory: false,
      },
    ]);
  };

  const handleRemoveItem = (id: string) => {
    if (items.length === 1) return;
    setItems(items.filter(item => item.id !== id));
  };

  const handleItemChange = (id: string, field: keyof RequestItemForm, value: any) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!requestTitle.trim()) {
      setError('כותרת הבקשה היא שדה חובה');
      return;
    }

    if (items.length === 0) {
      setError('יש להוסיף לפחות פריט אחד לבקשה');
      return;
    }

    const invalidItems = items.filter(item => item.targetFolderId === 0);
    if (invalidItems.length > 0) {
      setError('יש לבחור תיקיה לכל הפריטים');
      return;
    }

    setIsSubmitting(true);
    try {
      const dto: CreateFolderDocumentRequestDto = {
        clientId,
        requestTitle: requestTitle.trim(),
        generalInstructions: generalInstructions.trim(),
        accessCode: accessCode.trim() || undefined,
        items: items.map(item => ({
          targetFolderId: item.targetFolderId,
          specialInstructions: item.specialInstructions.trim(),
          isMandatory: item.isMandatory,
        })),
      };

      const createdRequest = await folderDocumentsApi.createRequest(dto);
      await folderDocumentsApi.sendRequest(createdRequest.id);
      onCreated();
    } catch (error: any) {
      const message = error.response?.data || 'שגיאה ביצירת הבקשה';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Track where mousedown started to prevent closing when selecting text
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <Overlay onMouseDown={handleMouseDown} onClick={handleOverlayClick}>
      <Modal>
        <Header>
          <Title>בקשת מסמכים חדשה</Title>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </Header>

        <Content>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="requestTitle">כותרת הבקשה *</Label>
              <Input
                id="requestTitle"
                type="text"
                value={requestTitle}
                onChange={(e) => setRequestTitle(e.target.value)}
                placeholder="למשל: מסמכים רפואיים לוועדה"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="generalInstructions">הנחיות כלליות</Label>
              <TextArea
                id="generalInstructions"
                value={generalInstructions}
                onChange={(e) => setGeneralInstructions(e.target.value)}
                placeholder="הנחיות שיופיעו בראש הבקשה..."
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="accessCode">קוד גישה</Label>
              <AccessCodeRow>
                <AccessCodeInput
                  id="accessCode"
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="XXXXXX"
                />
                <GenerateCodeButton type="button" onClick={handleGenerateCode}>
                  צור קוד
                </GenerateCodeButton>
              </AccessCodeRow>
              <HelpText>קוד גישה מגן על הבקשה. השאר ריק אם לא נדרש.</HelpText>
            </FormGroup>

            <Divider />

            <div>
              <SectionHeader>
                <SectionTitle>מסמכים נדרשים</SectionTitle>
                <ItemCount>{items.length} פריטים</ItemCount>
              </SectionHeader>

              <ItemsList>
                {items.map((item, index) => (
                  <ItemRow key={item.id}>
                    <ItemNumber>{index + 1}.</ItemNumber>
                    <ItemFields>
                      <ItemSelect
                        value={item.targetFolderId}
                        onChange={(e) => handleItemChange(item.id, 'targetFolderId', Number(e.target.value))}
                      >
                        <option value={0}>בחר תיקיה...</option>
                        {folders.map(folder => (
                          <option key={folder.id} value={folder.id}>
                            {folder.name}
                          </option>
                        ))}
                      </ItemSelect>
                      <ItemInput
                        type="text"
                        value={item.specialInstructions}
                        onChange={(e) => handleItemChange(item.id, 'specialInstructions', e.target.value)}
                        placeholder="הערות (אופציונלי)"
                      />
                    </ItemFields>
                    <MandatoryToggle
                      type="button"
                      $active={item.isMandatory}
                      onClick={() => handleItemChange(item.id, 'isMandatory', !item.isMandatory)}
                      title={item.isMandatory ? 'לחץ להפוך לאופציונלי' : 'לחץ להפוך לחובה'}
                    >
                      <Checkbox $active={item.isMandatory}>
                        {item.isMandatory && <CheckIcon />}
                      </Checkbox>
                      <ToggleLabel>חובה</ToggleLabel>
                    </MandatoryToggle>
                    {items.length > 1 && (
                      <RemoveButton
                        type="button"
                        onClick={() => handleRemoveItem(item.id)}
                        title="הסר מסמך"
                      >
                        <TrashIcon />
                      </RemoveButton>
                    )}
                  </ItemRow>
                ))}
              </ItemsList>

              <AddButtonWrapper>
                <AddButton type="button" onClick={handleAddItem}>
                  <PlusIcon />
                  הוסף מסמך
                </AddButton>
              </AddButtonWrapper>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}
          </Form>
        </Content>

        <Footer>
          <Button type="submit" variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'שולח בקשה...' : 'שלח בקשה ללקוח'}
          </Button>
          <Button type="button" onClick={onClose} disabled={isSubmitting}>
            ביטול
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default NewDocumentRequestModal;
